import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { InstanceStatus, InstanceSummary } from '@gm-binder/shared';
import { api, mediaUrl } from '../api/client';
import { AppShell } from '../components/layout/AppShell';
import {
  PageHeader,
  Card,
  Badge,
  Button,
  IconButton,
  MediaImage,
  LoadingState,
  EmptyState,
} from '../components/ui';

const STATUS_META: Record<InstanceStatus, { label: string; tone: 'success' | 'warn' | 'neutral' }> = {
  active: { label: 'In corso', tone: 'success' },
  paused: { label: 'In pausa', tone: 'warn' },
  completed: { label: 'Conclusa', tone: 'neutral' },
};

export function RunsPage() {
  const [runs, setRuns] = useState<InstanceSummary[] | null>(null);

  const reload = async () => {
    try {
      setRuns(await api.listInstances());
    } catch {
      setRuns([]);
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const remove = async (id: string) => {
    if (!confirm('Eliminare questa sessione salvata?')) return;
    await api.deleteInstance(id);
    setRuns((rs) => rs?.filter((r) => r.id !== id) ?? null);
  };

  return (
    <AppShell>
      <PageHeader
        eyebrow="Sessioni"
        title="Le tue sessioni"
        subtitle="Riprendi una partita in corso o rivedi quelle concluse."
      />
      {runs == null ? (
        <LoadingState />
      ) : runs.length === 0 ? (
        <EmptyState
          icon="⏳"
          title="Nessuna sessione"
          description="Avvia un'avventura dalla libreria per crearne una."
          actions={
            <Link to="/library">
              <Button variant="primary">Vai alla libreria</Button>
            </Link>
          }
        />
      ) : (
        <div className="runsGrid" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {runs.map((r) => {
            const meta = STATUS_META[r.status];
            return (
              <Card key={r.id} padded={false}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '52px 1fr auto auto',
                    gap: 'var(--space-4)',
                    alignItems: 'center',
                    padding: 'var(--space-3) var(--space-4)',
                  }}
                >
                  <MediaImage
                    src={mediaUrl(r.adventureSlug, r.adventureCover)}
                    alt={r.adventureTitle ?? ''}
                    fallback="🕯️"
                    style={{ width: 52, height: 52, borderRadius: 'var(--radius-md)' }}
                  />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontFamily: 'var(--font-display)' }}>{r.name}</div>
                    <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-dim)' }}>
                      {r.adventureTitle} · aggiornata il{' '}
                      {new Date(r.updatedAt).toLocaleDateString('it-IT')}
                    </div>
                  </div>
                  <Badge tone={meta.tone}>{meta.label}</Badge>
                  <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                    <Link to={`/run/${r.id}`}>
                      <Button variant="primary" size="sm">
                        Riprendi
                      </Button>
                    </Link>
                    <IconButton label="Elimina" size="sm" onClick={() => remove(r.id)}>
                      🗑️
                    </IconButton>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </AppShell>
  );
}
